import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Col, Row } from "react-bootstrap";
import scanService from "../services/scanService";
import "./Analysis.css";
import NiiVue from "../components/Shared/Niivue";
import ObjViewerMerged from "../components/ObjViewer";

// Generate scan timeline from patient data
const generateScanTimeline = (patient) => {
  if (!patient || !patient.latestScanData) {
    return [];
  }

  const scans = [];
  const latestTimepoint = patient.latestScanData.timepoint;
  const timepointNumber = parseInt(latestTimepoint.replace("T", ""));

  // Generate all scans up to the latest one
  for (let i = 1; i <= timepointNumber; i++) {
    const isLatest = i === timepointNumber;
    scans.push({
      id: `T${i}`,
      label: `T${i}`,
      date: patient.latestScanData.scanDate || new Date().toISOString().split("T")[0],
      status: isLatest ? "current" : "completed",
      color: isLatest ? "blue" : "green",
      isLatest: isLatest,
    });
  }

  // Add forecast for next scan
  scans.push({
    id: "forecast",
    label: "Forecast",
    date: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split("T")[0], // 1 year from now
    status: "prediction",
    color: "purple",
    isLatest: false,
  });

  return scans;
};

// Timeline Item Component
function TimelineItem({ item, isSelected, onSelect, activeTab }) {
  const getDotColor = () => {
    switch (item.color) {
      case "green":
        return "#28a745";
      case "blue":
        return "#007bff";
      case "purple":
        return "#6f42c1";
      default:
        return "#6c757d";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Check if this item should be highlighted as active
  const isActive = item.id === "forecast" ? activeTab === "forecast" : isSelected;

  return (
    <div
      className={`timeline-item ${isActive ? "selected" : ""}`}
      onClick={() => onSelect(item.id)}
    >
      <div className="timeline-dot" style={{ backgroundColor: getDotColor() }}></div>
      <div className="timeline-content">
        <div className="timeline-label">{item.label}</div>
        <div className="timeline-date">{formatDate(item.date)}</div>
        {item.status === "prediction" && (
          <button className="ai-prediction-btn">
            <span className="icon">📊</span>
            AI Prediction
          </button>
        )}
        {item.isLatest && <div className="latest-indicator">Latest</div>}
      </div>
    </div>
  );
}

// Forecast Component
function ForecastDisplay({ scanData }) {
  const forecast = scanData?.progressionForecast || {};
  const explanations = scanData?.forecastExplanations || {};

  // Check if we have meaningful forecast data
  const hasValidForecastData = forecast && Object.keys(forecast).length > 0;

  if (!hasValidForecastData) {
    return (
      <div className="no-forecast-message">
        <div className="no-forecast-icon">📊</div>
        <h4>No Forecast Available</h4>
        <p>
          Forecast predictions require baseline data for comparison. This appears to be a baseline
          scan.
        </p>
      </div>
    );
  }

  // Helper function to format feature names
  const formatFeatureName = (featureName) => {
    return featureName
      .replace(/_/g, " ")
      .replace(/\b\w/g, (l) => l.toUpperCase())
      .replace("Baseline ", "");
  };

  return (
    <>
      {/* Predicted Changes */}
      <div className="custom-card">
        <div className="custom-card-header">
          <div className="custom-card-header-text">
            <h3>Predicted Changes</h3>
          </div>
        </div>
        {forecast.predictedChangeInEdssScore && (
          <div className="biomarker-item">
            <div className="biomarker-label">EDSS Score Change - Forecast</div>
            <div
              className={`biomarker-value ${
                forecast.predictedChangeInEdssScore > 0 ? "increase" : "decrease"
              }`}
            >
              {forecast.predictedChangeInEdssScore > 0 ? "+" : ""}
              {forecast.predictedChangeInEdssScore.toFixed(2)}
            </div>
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{
                  width: `${Math.min(
                    (Math.abs(forecast.predictedChangeInEdssScore) / 2) * 100,
                    100
                  )}%`,
                }}
              ></div>
            </div>
            <div className="biomarker-description">
              {forecast.predictedChangeInEdssScore > 0
                ? "Disability progression predicted"
                : "Disability improvement predicted"}
            </div>
          </div>
        )}

        {forecast.predictedChangeInTotalBrainVolume_mm3 && (
          <div className="biomarker-item">
            <div className="biomarker-label">Brain Volume Change - Forecast</div>
            <div
              className={`biomarker-value ${
                forecast.predictedChangeInTotalBrainVolume_mm3 > 0 ? "increase" : "decrease"
              }`}
            >
              {forecast.predictedChangeInTotalBrainVolume_mm3 > 0 ? "+" : ""}
              {(forecast.predictedChangeInTotalBrainVolume_mm3 / 1000).toFixed(2)} mL
            </div>
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{
                  width: `${Math.min(
                    (Math.abs(forecast.predictedChangeInTotalBrainVolume_mm3) / 100000) * 100,
                    100
                  )}%`,
                }}
              ></div>
            </div>
            <div className="biomarker-description">
              {forecast.predictedChangeInTotalBrainVolume_mm3 > 0
                ? "Brain volume increase predicted"
                : "Brain volume decrease predicted"}
            </div>
          </div>
        )}

        {forecast.predictedChangeInLesionVolumeInWhiteMatter_mm3 && (
          <div className="biomarker-item">
            <div className="biomarker-label">White Matter Lesion Change - Forecast</div>
            <div
              className={`biomarker-value ${
                forecast.predictedChangeInLesionVolumeInWhiteMatter_mm3 > 0
                  ? "increase"
                  : "decrease"
              }`}
            >
              {forecast.predictedChangeInLesionVolumeInWhiteMatter_mm3 > 0 ? "+" : ""}
              {(forecast.predictedChangeInLesionVolumeInWhiteMatter_mm3 / 1000).toFixed(2)} mL
            </div>
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{
                  width: `${Math.min(
                    (Math.abs(forecast.predictedChangeInLesionVolumeInWhiteMatter_mm3) / 50000) *
                      100,
                    100
                  )}%`,
                }}
              ></div>
            </div>
            <div className="biomarker-description">
              {forecast.predictedChangeInLesionVolumeInWhiteMatter_mm3 > 0
                ? "White matter lesion growth predicted"
                : "White matter lesion reduction predicted"}
            </div>
          </div>
        )}

        {forecast.predictedChangeInLesionVolumeInGreyMatter_mm3 && (
          <div className="biomarker-item">
            <div className="biomarker-label">Grey Matter Lesion Change - Forecast</div>
            <div
              className={`biomarker-value ${
                forecast.predictedChangeInLesionVolumeInGreyMatter_mm3 > 0 ? "increase" : "decrease"
              }`}
            >
              {forecast.predictedChangeInLesionVolumeInGreyMatter_mm3 > 0 ? "+" : ""}
              {(forecast.predictedChangeInLesionVolumeInGreyMatter_mm3 / 1000).toFixed(2)} mL
            </div>
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{
                  width: `${Math.min(
                    (Math.abs(forecast.predictedChangeInLesionVolumeInGreyMatter_mm3) / 50000) *
                      100,
                    100
                  )}%`,
                }}
              ></div>
            </div>
            <div className="biomarker-description">
              {forecast.predictedChangeInLesionVolumeInGreyMatter_mm3 > 0
                ? "Grey matter lesion growth predicted"
                : "Grey matter lesion reduction predicted"}
            </div>
          </div>
        )}

        {forecast.predictedChangeInTotalLesionCount && (
          <div className="biomarker-item">
            <div className="biomarker-label">New Lesion Count - Forecast</div>
            <div
              className={`biomarker-value ${
                forecast.predictedChangeInTotalLesionCount > 0 ? "increase" : "decrease"
              }`}
            >
              {forecast.predictedChangeInTotalLesionCount > 0 ? "+" : ""}
              {forecast.predictedChangeInTotalLesionCount}
            </div>
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{
                  width: `${Math.min(
                    (Math.abs(forecast.predictedChangeInTotalLesionCount) / 10) * 100,
                    100
                  )}%`,
                }}
              ></div>
            </div>
            <div className="biomarker-description">
              {forecast.predictedChangeInTotalLesionCount > 0
                ? "New lesions predicted"
                : "Lesion resolution predicted"}
            </div>
          </div>
        )}
      </div>

      {/* Forecast Explanations */}
      {Object.keys(explanations).length > 0 && (
        <div className="custom-card mt-4">
          <div className="custom-card-header">
            <div className="custom-card-header-text">
              <h3>AI Explanation Factors</h3>
            </div>
          </div>
          {Object.entries(explanations).map(([metric, explanation]) => (
            <div key={metric} className="biomarker-item">
              <div className="biomarker-label">{formatFeatureName(metric)}</div>

              {explanation.aggravatingFactors && explanation.aggravatingFactors.length > 0 && (
                <div className="factor-group">
                  <div className="factor-label aggravating">⚠️ Aggravating Factors</div>
                  {explanation.aggravatingFactors.map((factor, index) => (
                    <div key={index} className="factor-item aggravating">
                      <span className="factor-name">{formatFeatureName(factor.Feature)}</span>
                      <span className="factor-value">+{factor.SHAP_Value.toFixed(3)}</span>
                    </div>
                  ))}
                </div>
              )}

              {explanation.mitigatingFactors && explanation.mitigatingFactors.length > 0 && (
                <div className="factor-group">
                  <div className="factor-label mitigating">✅ Mitigating Factors</div>
                  {explanation.mitigatingFactors.map((factor, index) => (
                    <div key={index} className="factor-item mitigating">
                      <span className="factor-name">{formatFeatureName(factor.Feature)}</span>
                      <span className="factor-value">{factor.SHAP_Value.toFixed(3)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </>
  );
}

// Data Display Component
function DataDisplay({ selectedTimepoint, scanData }) {
  if (!scanData) {
    return (
      <div className="custom-card">
        <div className="custom-card-header">
          <div className="custom-card-header-text">
            <h3>Loading scan data...</h3>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="analysis-wrapper">
      <div className="item">
        {/* <h3 className="">87%</h3> */}
        <p className="title"> Segmentation Status</p>
        <p className="">{scanData?.segmentationStatus || "Predicted (Ensemble)"}</p>
      </div>
      <div className="item ">
        <h3 className="">{scanData?.totalLesionCount || "N/A"}</h3>
        <p className="title">Total Lesion Count</p>
        <p className="">
          Total Volume:{" "}
          {scanData.totalLesionVolume
            ? `${(Number(scanData.totalLesionVolume) / 1000).toFixed(2)} mL`
            : "N/A"}
        </p>
      </div>
      <div className="item ">
        <h3 className="">
          {scanData.totalBrainVolume
            ? `${(Number(scanData.totalBrainVolume) / 1000).toFixed(0)}`
            : "N/A"}
        </h3>
        <p className="title">Brain Volume (mm³)</p>
        <p className="text-orange">
          {" "}
          Grey Matter:{" "}
          {scanData.greyMatterVolume
            ? `${(Number(scanData.greyMatterVolume) / 1000).toFixed(0)} mL`
            : "N/A"}{" "}
          | White Matter:{" "}
          {scanData.whiteMatterVolume
            ? `${(Number(scanData.whiteMatterVolume) / 1000).toFixed(0)} mL`
            : "N/A"}
        </p>
      </div>
      <div className="item ">
        <h3 className="">
          {scanData.processingTime ? `${Number(scanData.processingTime).toFixed(1)}s` : "N/A"}
        </h3>
        <p className="title">Processing Time</p>
        <p className="">
          Analysis completed on{" "}
          {scanData.analysisTimestamp
            ? new Date(scanData.analysisTimestamp).toLocaleString()
            : "N/A"}
        </p>
      </div>
    </div>
  );
}

export default function Analysis() {
  const navigate = useNavigate();
  const [selectedTimepoint, setSelectedTimepoint] = useState(null);
  const [scanData, setScanData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [scanTimeline, setScanTimeline] = useState([]);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [zoomLevels, setZoomLevels] = useState({
    axial: 1,
    coronal: 1,
    sagittal: 1,
    rotation: 1,
  });
  const [isGifPaused, setIsGifPaused] = useState(false);
  const [activeTab, setActiveTab] = useState("analysis");
  const [latestScanForecastData, setLatestScanForecastData] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageZoom, setImageZoom] = useState(1);

  // Refs for wheel zoom panels
  const axialPanelRef = useRef(null);
  const coronalPanelRef = useRef(null);
  const sagittalPanelRef = useRef(null);
  const rotationPanelRef = useRef(null);

  // Debug: Log when component mounts
  useEffect(() => {
    // Force clear any potential cached content
    setTimeout(() => {
      const fileViewer = document.querySelector(".file-viewer");
      if (fileViewer) {
        // Clear any text content that might be showing binary data
        const textNodes = Array.from(fileViewer.childNodes).filter(
          (node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim()
        );
        textNodes.forEach((node) => node.remove());

        // Also clear any canvas elements that might be showing binary data
        const canvasElements = fileViewer.querySelectorAll("canvas");
        canvasElements.forEach((canvas) => {
          if (canvas.parentNode) {
            canvas.parentNode.removeChild(canvas);
          }
        });

        // Force clear any binary data that might be showing
        const allText = fileViewer.textContent;
        if (allText.includes("PNG") || allText.includes("IHDR") || allText.includes("IDAT")) {
          fileViewer.innerHTML = "";
          // Re-render the placeholder
          fileViewer.innerHTML = `
            <div class="brain-viewer-placeholder">
              <div class="placeholder-content">
                <div class="placeholder-icon">🧠</div>
                <h4>3D Brain Viewer</h4>
                <p>Advanced medical imaging visualization</p>
                <div class="debug-info" style="font-size: 12px; color: #6c757d; margin-bottom: 10px;">
                  ✅ Niivue component disabled - No binary data
                </div>
                <div class="debug-info" style="font-size: 12px; color: #dc3545; margin-bottom: 10px;">
                  🔍 If you see binary data, it's coming from browser cache - Hard refresh needed!
                </div>
                <div class="viewer-features">
                  <div class="feature-item">
                    <span class="feature-icon">🔍</span>
                    <span>Interactive 3D Navigation</span>
                  </div>
                  <div class="feature-item">
                    <span class="feature-icon">📊</span>
                    <span>Lesion Detection & Analysis</span>
                  </div>
                  <div class="feature-item">
                    <span class="feature-icon">🎯</span>
                    <span>Biomarker Visualization</span>
                  </div>
                </div>
                <div class="viewer-status">
                  <span class="status-indicator">●</span>
                  <span>Ready for scan data</span>
                </div>
              </div>
            </div>
          `;
        }
      }
    }, 100);
  }, []);

  // Load selected patient data on component mount
  useEffect(() => {
    const patientData = sessionStorage.getItem("selectedPatient");
    if (patientData) {
      try {
        const patient = JSON.parse(patientData);
        setSelectedPatient(patient);
        // Generate scan timeline from patient data
        const timeline = generateScanTimeline(patient);
        setScanTimeline(timeline);

        // Set the latest scan as selected by default
        if (timeline.length > 0) {
          const latestScan =
            timeline.find((scan) => scan.isLatest) || timeline[timeline.length - 2]; // -2 to exclude forecast
          if (latestScan) {
            setSelectedTimepoint(latestScan.id);

            // Scroll to the latest scan after a short delay to ensure DOM is ready
            setTimeout(() => {
              scrollToLatestScan();
            }, 100);
          }
        }
      } catch (error) {
        console.error("Error parsing patient data:", error);
        // Redirect to dashboard if patient data is invalid
        navigate("/admin");
      }
    } else {
      navigate("/admin");
    }
  }, [navigate]);

  // Fetch scan data when timepoint changes
  useEffect(() => {
    const fetchScanData = async () => {
      setLoading(true);
      try {
        // Use selected patient's latest scan data if available
        if (selectedPatient) {
          // Use the correct patient ID field
          const patientId = selectedPatient._id || selectedPatient.id;

          if (!patientId) {
            console.error("No patient ID found in selectedPatient:", selectedPatient);
            setLoading(false);
            return;
          }

          // Fetch all scans for the patient to get specific timepoint data
          const allScansResponse = await scanService.getScansByCustomer(patientId);

          if (allScansResponse.success && allScansResponse.data) {
            // Find the scan that matches the selected timepoint
            const targetScan = allScansResponse.data.find(
              (scan) =>
                scan.timepoint === selectedTimepoint ||
                scan.timepoint === selectedTimepoint.replace("T", "T")
            );

            if (targetScan) {
              // Fetch analysis results for the specific scan
              const response = await scanService.getAnalysisResults(targetScan._id);

              if (response.success && response.data.analysisResult) {
                const analysisResult = response.data.analysisResult;
                const consolidatedResult = analysisResult.consolidated_results?.[0];

                // Extract comprehensive data from the analysis result
                const biomarkers = consolidatedResult?.biomarkers || {};

                const scanData = {
                  segmentationStatus:
                    consolidatedResult?.segmentationPrediction?.status || "Predicted (Ensemble)",
                  confidenceScore: consolidatedResult?.segmentationPrediction?.confidence_score,
                  totalLesionCount: biomarkers.totalLesionCount,
                  totalLesionVolume: biomarkers.totalLesionVolume_mm3,
                  totalBrainVolume: biomarkers.totalBrainVolume_mm3,
                  greyMatterVolume: biomarkers.greyMatterVolume_mm3,
                  whiteMatterVolume: biomarkers.whiteMatterVolume_mm3,
                  processingTime: analysisResult.processing_time_seconds,
                  analysisTimestamp: analysisResult.analysis_timestamp,
                  patientId: selectedPatient?.patientId || analysisResult.patient_id || "PT000",
                  timepoint: analysisResult.timepoint,
                  // Add visualization URLs
                  visualization3dRotationVideo:
                    consolidatedResult?.assetPaths?.visualization3dRotationVideo,
                  visualizationSliceAxialPng:
                    consolidatedResult?.assetPaths?.visualizationSliceAxialPng,
                  visualizationSliceCoronalPng:
                    consolidatedResult?.assetPaths?.visualizationSliceCoronalPng,
                  visualizationSliceSagittalPng:
                    consolidatedResult?.assetPaths?.visualizationSliceSagittalPng,
                  visualizationMultiplanarPng:
                    consolidatedResult?.assetPaths?.visualizationMultiplanarPng,
                  visualizationOverlay3dNifti:
                    consolidatedResult?.assetPaths?.visualizationOverlay3dNifti,

                  // Get OBJ file url
                  meshBrain3D: consolidatedResult?.assetPaths?.meshBrain3D,
                  meshLesions3D: consolidatedResult?.assetPaths?.meshLesions3D,

                  // Additional fields for Figma design - extract from biomarkers
                  brainAtrophyRate: biomarkers.annualizedBrainVolumeLoss_mm3_per_year
                    ? (Number(biomarkers.annualizedBrainVolumeLoss_mm3_per_year) / 1000).toFixed(2)
                    : biomarkers.brainAtrophyRate || biomarkers.atrophyRate || "N/A",
                  whiteMatterLesionVolume: biomarkers.lesionVolumeInWhiteMatter_mm3,
                  greyMatterLesionVolume: biomarkers.lesionVolumeInGreyMatter_mm3,
                  whiteMatterIntensity: biomarkers.averageT1BlackHoleIntensity,
                  // Additional biomarker fields that might exist
                  lesionLoad: biomarkers.totalLesionVolume_mm3,
                  atrophyRate: biomarkers.annualizedBrainVolumeLoss_mm3_per_year
                    ? (Number(biomarkers.annualizedBrainVolumeLoss_mm3_per_year) / 1000).toFixed(2)
                    : biomarkers.atrophyRate || biomarkers.brainAtrophyRate || "N/A",
                  lesionDistribution: biomarkers.lesionDistribution,
                  tissueIntegrity: biomarkers.tissueIntegrity,
                  // Change from baseline metrics
                  changeFromBaseline_totalLesionVolume:
                    biomarkers.changeFromBaseline_totalLesionVolume_mm3,
                  changeFromBaseline_totalLesionCount:
                    biomarkers.changeFromBaseline_totalLesionCount,
                  changeFromBaseline_totalBrainVolume:
                    biomarkers.changeFromBaseline_totalBrainVolume_mm3,
                  changeFromBaseline_greyMatterVolume:
                    biomarkers.changeFromBaseline_greyMatterVolume_mm3,
                  changeFromBaseline_whiteMatterVolume:
                    biomarkers.changeFromBaseline_whiteMatterVolume_mm3,
                  changeFromBaseline_averageT1BlackHoleIntensity:
                    biomarkers.changeFromBaseline_averageT1BlackHoleIntensity,
                  // Additional ratios and metrics
                  greyMatterLesionRatio: biomarkers.greyMatterLesionRatio,
                  // Patient information from analysis
                  ageAtScan: consolidatedResult?.patientInformation?.ageAtScan,
                  sex: consolidatedResult?.patientInformation?.sex,
                  msSubtype: consolidatedResult?.patientInformation?.msSubtype,
                  edssScore: consolidatedResult?.patientInformation?.edssScore,
                  // Forecast data
                  progressionForecast: analysisResult.progressionForecast,
                  forecastExplanations: analysisResult.forecastExplanations,
                  hasForecastData: !!(
                    analysisResult.progressionForecast &&
                    Object.keys(analysisResult.progressionForecast).length > 0
                  ),
                };

                setScanData(scanData);

                // Store forecast data from latest scan separately
                if (
                  analysisResult.progressionForecast &&
                  Object.keys(analysisResult.progressionForecast).length > 0
                ) {
                  const forecastImages = {
                    shapPlotForecast_EDSS: consolidatedResult?.assetPaths?.shapPlotForecast_EDSS,
                    shapPlotForecast_LesionVolWM:
                      consolidatedResult?.assetPaths?.shapPlotForecast_LesionVolWM,
                    shapPlotForecast_LesionVolGM:
                      consolidatedResult?.assetPaths?.shapPlotForecast_LesionVolGM,
                    shapPlotForecast_TotalBrainVolume:
                      consolidatedResult?.assetPaths?.shapPlotForecast_TotalBrainVolume,
                    shapPlotForecast_GreyMatterVolume:
                      consolidatedResult?.assetPaths?.shapPlotForecast_GreyMatterVolume,
                    shapPlotForecast_WhiteMatterVolume:
                      consolidatedResult?.assetPaths?.shapPlotForecast_WhiteMatterVolume,
                    shapPlotForecast_Count: consolidatedResult?.assetPaths?.shapPlotForecast_Count,
                    shapPlotForecast_Volume:
                      consolidatedResult?.assetPaths?.shapPlotForecast_Volume,
                    predictedLesionMaskNifti:
                      consolidatedResult?.assetPaths?.predictedLesionMaskNifti,
                  };

                  setLatestScanForecastData({
                    progressionForecast: analysisResult.progressionForecast,
                    forecastExplanations: analysisResult.forecastExplanations,
                    timepoint: analysisResult.timepoint,
                    patientId: analysisResult.patient_id,
                    forecastImages: forecastImages,
                  });
                }
              } else {
                // No analysis results for this specific scan, use mock data
                const mockData = {
                  segmentationStatus: "N/A",
                  confidenceScore: "N/A",
                  totalLesionCount: "N/A",
                  totalLesionVolume: "N/A",
                  totalBrainVolume: "N/A",
                  greyMatterVolume: "N/A",
                  whiteMatterVolume: "N/A",
                  processingTime: "N/A",
                  analysisTimestamp: "N/A",
                  patientId: selectedPatient?.patientId || "N/A",
                  timepoint: selectedTimepoint || "N/A",
                  // Mock visualization URLs
                  visualization3dRotationVideo:
                    "https://via.placeholder.com/400x300/007bff/ffffff?text=3D+Rotation+Video",
                  visualizationSliceAxialPng:
                    "https://via.placeholder.com/300x200/28a745/ffffff?text=Axial+Slice",
                  visualizationSliceCoronalPng:
                    "https://via.placeholder.com/300x200/ffc107/ffffff?text=Coronal+Slice",
                  visualizationSliceSagittalPng:
                    "https://via.placeholder.com/300x200/dc3545/ffffff?text=Sagittal+Slice",
                  visualizationMultiplanarPng:
                    "https://via.placeholder.com/400x300/6f42c1/ffffff?text=Multiplanar+View",
                  // Additional fields for Figma design
                  brainAtrophyRate: "-4.00",
                  whiteMatterLesionVolume: 2150,
                  greyMatterLesionVolume: 300,
                  whiteMatterIntensity: "265",
                };
                setScanData(mockData);
              }
            } else {
              // No scan found for this timepoint, use mock data
              const mockData = {
                segmentationStatus: "Predicted (Ensemble)",
                confidenceScore: 0.85,
                totalLesionCount: 15,
                totalLesionVolume: 28178,
                totalBrainVolume: 891529,
                greyMatterVolume: 368010,
                whiteMatterVolume: 523519,
                processingTime: 346.95,
                analysisTimestamp: new Date().toISOString(),
                patientId: selectedPatient?.patientId || "PT000",
                timepoint: selectedTimepoint || "T1",
                // Mock visualization URLs
                visualization3dRotationVideo: "N/A",
                visualizationSliceAxialPng: "N/A",
                visualizationSliceCoronalPng: "N/A",
                visualizationSliceSagittalPng: "N/A",
                visualizationMultiplanarPng: "N/A",
                visualizationOverlay3dNifti: "N/A",
                // Additional fields for Figma design
                brainAtrophyRate: "N/A",
                whiteMatterLesionVolume: "N/A",
                greyMatterLesionVolume: "N/A",
                whiteMatterIntensity: "N/A",
              };
              setScanData(mockData);
            }
          } else {
            // Fallback to mock data if no analysis results
            const mockData = {
              segmentationStatus: "N/A",
              confidenceScore: "N/A",
              totalLesionCount: "N/A",
              totalLesionVolume: "N/A",
              totalBrainVolume: "N/A",
              greyMatterVolume: "N/A",
              whiteMatterVolume: "N/A",
              processingTime: "N/A",
              analysisTimestamp: "N/A",
              patientId: "N/A",
              timepoint: selectedTimepoint || "N/A",
              // Mock visualization URLs
              visualization3dRotationVideo: "N/A",
              visualizationSliceAxialPng: "N/A",
              visualizationSliceCoronalPng: "N/A",
              visualizationSliceSagittalPng: "N/A",
              visualizationMultiplanarPng: "N/A",
            };
            setScanData(mockData);
          }
        } else {
          // No patient selected or no scan data, use mock data
          const mockData = {
            segmentationStatus: "N/A",
            confidenceScore: "N/A",
            totalLesionCount: "N/A",
            totalLesionVolume: "N/A",
            totalBrainVolume: "N/A",
            greyMatterVolume: "N/A",
            whiteMatterVolume: "N/A",
            processingTime: "N/A",
            analysisTimestamp: "N/A",
            patientId: selectedPatient?.patientId || "N/A",
            timepoint: selectedTimepoint || "N/A",
            // Mock visualization URLs
            visualization3dRotationVideo: "N/A",
            visualizationSliceAxialPng: "N/A",
            visualizationSliceCoronalPng: "N/A",
            visualizationSliceSagittalPng: "N/A",
            visualizationMultiplanarPng: "N/A",
          };
          setScanData(mockData);
        }
      } catch (error) {
        console.error("Error fetching scan data:", error);
        // Use mock data as fallback
        const mockData = {
          segmentationStatus: "N/A",
          confidenceScore: "N/A",
          totalLesionCount: "N/A",
          totalLesionVolume: "N/A",
          totalBrainVolume: "N/A",
          greyMatterVolume: "N/A",
          whiteMatterVolume: "N/A",
          processingTime: "N/A",
          analysisTimestamp: "N/A",
          patientId: "N/A",
          timepoint: selectedTimepoint || "N/A",
          // Mock visualization URLs
          visualization3dRotationVideo: "N/A",
          visualizationSliceAxialPng: "N/A",
          visualizationSliceCoronalPng: "N/A",
          visualizationSliceSagittalPng: "N/A",
          visualizationMultiplanarPng: "N/A",
        };
        setScanData(mockData);
      } finally {
        setLoading(false);
      }
    };

    fetchScanData();
  }, [selectedTimepoint, selectedPatient]);

  // Scroll to latest scan when timeline is ready
  useEffect(() => {
    if (scanTimeline.length > 0 && selectedTimepoint) {
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        scrollToLatestScan();
      }, 200);
    }
  }, [scanTimeline, selectedTimepoint]);

  // Attach wheel event listeners with passive: false to allow preventDefault
  useEffect(() => {
    const handleWheel = (e, panelType) => {
      e.preventDefault();
      e.stopPropagation();
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      setZoomLevels((prev) => ({
        ...prev,
        [panelType]: Math.max(0.5, Math.min(3, prev[panelType] + delta)),
      }));
    };

    const axialPanel = axialPanelRef.current;
    const coronalPanel = coronalPanelRef.current;
    const sagittalPanel = sagittalPanelRef.current;
    const rotationPanel = rotationPanelRef.current;

    const handlers = [];

    if (axialPanel) {
      const handler = (e) => handleWheel(e, "axial");
      axialPanel.addEventListener("wheel", handler, { passive: false });
      handlers.push({ element: axialPanel, handler });
    }
    if (coronalPanel) {
      const handler = (e) => handleWheel(e, "coronal");
      coronalPanel.addEventListener("wheel", handler, { passive: false });
      handlers.push({ element: coronalPanel, handler });
    }
    if (sagittalPanel) {
      const handler = (e) => handleWheel(e, "sagittal");
      sagittalPanel.addEventListener("wheel", handler, { passive: false });
      handlers.push({ element: sagittalPanel, handler });
    }
    if (rotationPanel) {
      const handler = (e) => handleWheel(e, "rotation");
      rotationPanel.addEventListener("wheel", handler, { passive: false });
      handlers.push({ element: rotationPanel, handler });
    }

    // Cleanup function
    return () => {
      handlers.forEach(({ element, handler }) => {
        if (element) {
          element.removeEventListener("wheel", handler);
        }
      });
    };
  }, [scanData]); // Re-run when scanData changes so refs are available

  const handleTimepointSelect = (timepointId) => {
    if (timepointId === "forecast") {
      setActiveTab("forecast");
    } else {
      setActiveTab("analysis");
      setSelectedTimepoint(timepointId);
    }
  };

  // Image modal functions
  const openImageModal = (imageUrl, imageTitle) => {
    setSelectedImage({ url: imageUrl, title: imageTitle });
    setImageZoom(1);
  };

  const closeImageModal = () => {
    setSelectedImage(null);
    setImageZoom(1);
  };

  const zoomIn = () => {
    setImageZoom((prev) => Math.min(prev + 0.25, 3));
  };

  const zoomOut = () => {
    setImageZoom((prev) => Math.max(prev - 0.25, 0.5));
  };

  const resetZoom = () => {
    setImageZoom(1);
  };

  const handleGifHover = (isHovering) => {
    setIsGifPaused(isHovering);
  };

  const handleScroll = (direction) => {
    const timelineContainer = document.querySelector(".scan-timeline");
    if (timelineContainer) {
      // Scroll by one scan width (200px + 16px gap = 216px)
      const scrollAmount = 216; // One scan width + gap
      const maxScroll = timelineContainer.scrollWidth - timelineContainer.clientWidth;
      const newPosition =
        direction === "left"
          ? Math.max(0, scrollPosition - scrollAmount)
          : Math.min(maxScroll, scrollPosition + scrollAmount);

      setScrollPosition(newPosition);
      timelineContainer.scrollTo({
        left: newPosition,
        behavior: "smooth",
      });
    }
  };

  const scrollToLatestScan = () => {
    const timelineContainer = document.querySelector(".scan-timeline");
    if (timelineContainer) {
      // Find the latest scan element
      const latestScanElement = timelineContainer.querySelector(".timeline-item.selected");
      if (latestScanElement) {
        // Calculate the position to center the latest scan
        const containerWidth = timelineContainer.clientWidth;
        const elementLeft = latestScanElement.offsetLeft;
        const elementWidth = latestScanElement.offsetWidth;
        const scrollLeft = elementLeft - containerWidth / 2 + elementWidth / 2;

        // Scroll to the calculated position
        timelineContainer.scrollTo({
          left: Math.max(0, scrollLeft),
          behavior: "smooth",
        });

        // Update scroll position state
        setScrollPosition(Math.max(0, scrollLeft));
      }
    }
  };

  if (loading) {
    return (
      <div className="analysis">
        <div className="loading-container">
          <div className="spinner-border text-primary" role="status"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="analysis">
      {/* AI-Powered Imaging Analysis Section */}
      <div className="custom-card">
        <div className="custom-card-header mb-0">
          <div className="custom-card-header-text">
            <h2>AI-Powered Imaging Analysis</h2>
            <p>Advanced lesion detection and early MS biomarker identification</p>
          </div>
        </div>
      </div>
      <div className="custom-card mt-4">
        <div className="custom-card-header">
          <div className="custom-card-header-text">
            <h3>Scan Timeline</h3>
          </div>
        </div>
        <div className="scan-timeline-container">
          <div className="scan-timeline">
            {scanTimeline.map((item, index) => (
              <React.Fragment key={item.id}>
                <TimelineItem
                  item={item}
                  isSelected={selectedTimepoint === item.id}
                  onSelect={handleTimepointSelect}
                  activeTab={activeTab}
                />
                {index < scanTimeline.length - 1 && (
                  <div className="timeline-arrow">
                    <span className="icon">→</span>
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
      <div className="custom-card mt-4">
        {/* Dynamic Data Display */}
        <DataDisplay selectedTimepoint={selectedTimepoint} scanData={scanData} loading={loading} />
      </div>

      {/* 3D Brain Viewer and Additional Content */}
      <Row className="mt-4">
        <Col md={7}>
          <div className="custom-card">
            <div className="custom-card-header">
              <div className="custom-card-header-text">
                <h3>3D Brain Viewer</h3>
              </div>
            </div>
            <div className="file-viewer">
              {scanData?.visualization3dRotationVideo &&
              scanData.visualization3dRotationVideo !== "N/A" ? (
                <div className="brain-viewer-container">
                  <div className="brain-viewer-grid">
                    {/* Axial View */}
                    <div className="viewer-panel axial-view">
                      <div className="panel-header">
                        <span className="view-label">Axial</span>
                        <span className="orientation-markers">
                          <span className="marker">L</span>
                          <span className="marker">R</span>
                        </span>
                      </div>
                      <div
                        ref={axialPanelRef}
                        className="panel-content clickable-image"
                        onClick={() => {
                          if (
                            scanData.visualizationSliceAxialPng &&
                            scanData.visualizationSliceAxialPng !== "N/A"
                          ) {
                            openImageModal(scanData.visualizationSliceAxialPng, "Axial View");
                          }
                        }}
                      >
                        <img
                          src={
                            scanData.visualizationSliceAxialPng &&
                            scanData.visualizationSliceAxialPng !== "N/A"
                              ? scanData.visualizationSliceAxialPng
                              : "https://via.placeholder.com/200x200/28a745/ffffff?text=No+Data"
                          }
                          alt="Axial View"
                          className="brain-slice-image"
                          style={{
                            transform: `scale(${zoomLevels.axial})`,
                            transformOrigin: "center",
                            transition: "transform 0.1s ease",
                          }}
                        />
                        <div className="crosshair horizontal"></div>
                        <div className="crosshair vertical"></div>
                      </div>
                    </div>

                    {/* Coronal View */}
                    <div className="viewer-panel coronal-view">
                      <div className="panel-header">
                        <span className="view-label">Coronal</span>
                        <span className="orientation-markers">
                          <span className="marker">S</span>
                          <span className="marker">I</span>
                        </span>
                      </div>
                      <div
                        ref={coronalPanelRef}
                        className="panel-content clickable-image"
                        onClick={() => {
                          if (
                            scanData.visualizationSliceCoronalPng &&
                            scanData.visualizationSliceCoronalPng !== "N/A"
                          ) {
                            openImageModal(scanData.visualizationSliceCoronalPng, "Coronal View");
                          }
                        }}
                      >
                        <img
                          src={
                            scanData.visualizationSliceCoronalPng &&
                            scanData.visualizationSliceCoronalPng !== "N/A"
                              ? scanData.visualizationSliceCoronalPng
                              : "https://via.placeholder.com/200x200/ffc107/ffffff?text=No+Data"
                          }
                          alt="Coronal View"
                          className="brain-slice-image"
                          style={{
                            transform: `scale(${zoomLevels.coronal})`,
                            transformOrigin: "center",
                            transition: "transform 0.1s ease",
                          }}
                        />
                        <div className="crosshair horizontal"></div>
                        <div className="crosshair vertical"></div>
                      </div>
                    </div>

                    {/* Sagittal View */}
                    <div className="viewer-panel sagittal-view">
                      <div className="panel-header">
                        <span className="view-label">Sagittal</span>
                        <span className="orientation-markers">
                          <span className="marker">P</span>
                          <span className="marker">A</span>
                        </span>
                      </div>
                      <div
                        ref={sagittalPanelRef}
                        className="panel-content clickable-image"
                        onClick={() => {
                          if (
                            scanData.visualizationSliceSagittalPng &&
                            scanData.visualizationSliceSagittalPng !== "N/A"
                          ) {
                            openImageModal(scanData.visualizationSliceSagittalPng, "Sagittal View");
                          }
                        }}
                      >
                        <img
                          src={
                            scanData.visualizationSliceSagittalPng &&
                            scanData.visualizationSliceSagittalPng !== "N/A"
                              ? scanData.visualizationSliceSagittalPng
                              : "https://via.placeholder.com/200x200/dc3545/ffffff?text=No+Data"
                          }
                          alt="Sagittal View"
                          className="brain-slice-image"
                          style={{
                            transform: `scale(${zoomLevels.sagittal})`,
                            transformOrigin: "center",
                            transition: "transform 0.1s ease",
                          }}
                        />
                        <div className="crosshair horizontal"></div>
                        <div className="crosshair vertical"></div>
                      </div>
                    </div>

                    {/* 3D Rotation GIF */}
                    <div className="viewer-panel rotation-view">
                      <div className="panel-header">
                        <span className="view-label">3D Rotation</span>
                        <span className="timepoint-badge">{scanData.timepoint}</span>
                      </div>
                      <div
                        ref={rotationPanelRef}
                        className="panel-content clickable-image"
                        onMouseEnter={() => handleGifHover(true)}
                        onMouseLeave={() => handleGifHover(false)}
                        onClick={() => {
                          if (
                            scanData.visualization3dRotationVideo &&
                            scanData.visualization3dRotationVideo !== "N/A"
                          ) {
                            openImageModal(
                              scanData.visualization3dRotationVideo,
                              "3D Brain Rotation"
                            );
                          }
                        }}
                      >
                        <img
                          src={
                            scanData.visualization3dRotationVideo &&
                            scanData.visualization3dRotationVideo !== "N/A"
                              ? scanData.visualization3dRotationVideo
                              : "https://via.placeholder.com/300x200/6f42c1/ffffff?text=No+Data"
                          }
                          alt="3D Brain Rotation"
                          className="brain-rotation-gif"
                          style={{
                            transform: `scale(${zoomLevels.rotation})`,
                            transformOrigin: "center",
                            transition: "transform 0.1s ease",
                            animationPlayState: isGifPaused ? "paused" : "running",
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="viewer-info">
                    <h5>Multiplanar Brain Visualization - {scanData.timepoint}</h5>
                    <p>
                      Interactive cross-sectional views with synchronized crosshairs showing
                      anatomical orientation
                    </p>
                  </div>
                </div>
              ) : (
                <div className="brain-viewer-placeholder">
                  <div className="placeholder-content">
                    <div className="placeholder-icon">🧠</div>
                    <h4>3D Brain Viewer</h4>
                    <p>Advanced medical imaging visualization</p>
                    <div
                      className="debug-info"
                      style={{ fontSize: "12px", color: "#6c757d", marginBottom: "10px" }}
                    >
                      ✅ Niivue component disabled - No binary data
                    </div>
                    <div
                      className="debug-info"
                      style={{ fontSize: "12px", color: "#dc3545", marginBottom: "10px" }}
                    >
                      🔍 If you see binary data, it's coming from browser cache - Hard refresh
                      needed!
                    </div>
                    <div className="viewer-features">
                      <div className="feature-item">
                        <span className="feature-icon">🔍</span>
                        <span>Interactive 3D Navigation</span>
                      </div>
                      <div className="feature-item">
                        <span className="feature-icon">📊</span>
                        <span>Lesion Detection & Analysis</span>
                      </div>
                      <div className="feature-item">
                        <span className="feature-icon">🎯</span>
                        <span>Biomarker Visualization</span>
                      </div>
                    </div>
                    <div className="viewer-status">
                      <span className="status-indicator">●</span>
                      <span>Ready for scan data</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          {/* 3D Mesh Viewer Section */}
          <div className="custom-card mt-4">
            <div className="custom-card-header">
              <div className="custom-card-header-text">
                <h3>3D Mesh Viewer</h3>
                <p>Interactive 3D models of brain and lesions</p>
              </div>
            </div>
            <div className="file-viewer">
              <ObjViewerMerged
                brainUrl={scanData?.meshBrain3D}
                lesionUrl={scanData?.meshLesions3D}
              />
            </div>
          </div>
          {/* 3D Overlay NIfTI Viewer - New Section */}
          {/* <div className="custom-card mt-4">
            <div className="custom-card-header">
              <div className="custom-card-header-text">
                <h3>3D Overlay NIfTI Viewer</h3>
                <p>Interactive 3D view of the overlay volume with basic controls</p>
              </div>
            </div>
            <div className="file-viewer">
              {scanData?.visualizationOverlay3dNifti &&
              scanData.visualizationOverlay3dNifti !== "N/A" ? (
                <>
                  <NiiVue imageUrl={scanData.visualizationOverlay3dNifti} />
                </>
              ) : (
                <div className="brain-viewer-placeholder">
                  <div className="placeholder-content">
                    <div className="placeholder-icon">🧠</div>
                    <h4>3D Overlay NIfTI Viewer</h4>
                    <p>No overlay NIfTI available for this scan</p>
                  </div>
                </div>
              )}
            </div>
          </div> */}

          {/* Forecast Images Section - Only show when Forecast tab is active */}
          {activeTab === "forecast" && latestScanForecastData?.forecastImages && (
            <div className="custom-card mt-4">
              <div className="custom-card-header">
                <div className="custom-card-header-text">
                  <h3>
                    <span className="icon">🔮</span>
                    AI Forecast Visualization
                  </h3>
                  <p>SHAP plots showing AI prediction factors and explanations</p>
                </div>
              </div>
              <div className="forecast-images-container">
                <div className="forecast-images-grid">
                  {/* EDSS Forecast */}
                  {latestScanForecastData.forecastImages.shapPlotForecast_EDSS && (
                    <div className="forecast-image-item">
                      <div className="forecast-image-header">
                        <span className="forecast-icon">📊</span>
                        <span className="forecast-label">EDSS Score Prediction</span>
                      </div>
                      <div
                        className="forecast-image-content"
                        onClick={() =>
                          openImageModal(
                            latestScanForecastData.forecastImages.shapPlotForecast_EDSS,
                            "EDSS Score Prediction"
                          )
                        }
                      >
                        <img
                          src={latestScanForecastData.forecastImages.shapPlotForecast_EDSS}
                          alt="EDSS Score Forecast"
                          className="forecast-shap-image"
                        />
                        <div className="image-overlay">
                          <span className="zoom-icon">🔍</span>
                          <span className="zoom-text">Click to expand</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Brain Volume Forecast */}
                  {latestScanForecastData.forecastImages.shapPlotForecast_TotalBrainVolume && (
                    <div className="forecast-image-item">
                      <div className="forecast-image-header">
                        <span className="forecast-icon">🧠</span>
                        <span className="forecast-label">Brain Volume Prediction</span>
                      </div>
                      <div
                        className="forecast-image-content"
                        onClick={() =>
                          openImageModal(
                            latestScanForecastData.forecastImages.shapPlotForecast_TotalBrainVolume,
                            "Brain Volume Prediction"
                          )
                        }
                      >
                        <img
                          src={
                            latestScanForecastData.forecastImages.shapPlotForecast_TotalBrainVolume
                          }
                          alt="Brain Volume Forecast"
                          className="forecast-shap-image"
                        />
                        <div className="image-overlay">
                          <span className="zoom-icon">🔍</span>
                          <span className="zoom-text">Click to expand</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* White Matter Lesion Forecast */}
                  {latestScanForecastData.forecastImages.shapPlotForecast_LesionVolWM && (
                    <div className="forecast-image-item">
                      <div className="forecast-image-header">
                        <span className="forecast-icon">⚪</span>
                        <span className="forecast-label">White Matter Lesion Prediction</span>
                      </div>
                      <div
                        className="forecast-image-content"
                        onClick={() =>
                          openImageModal(
                            latestScanForecastData.forecastImages.shapPlotForecast_LesionVolWM,
                            "White Matter Lesion Prediction"
                          )
                        }
                      >
                        <img
                          src={latestScanForecastData.forecastImages.shapPlotForecast_LesionVolWM}
                          alt="White Matter Lesion Forecast"
                          className="forecast-shap-image"
                        />
                        <div className="image-overlay">
                          <span className="zoom-icon">🔍</span>
                          <span className="zoom-text">Click to expand</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Grey Matter Lesion Forecast */}
                  {latestScanForecastData.forecastImages.shapPlotForecast_LesionVolGM && (
                    <div className="forecast-image-item">
                      <div className="forecast-image-header">
                        <span className="forecast-icon">🔘</span>
                        <span className="forecast-label">Grey Matter Lesion Prediction</span>
                      </div>
                      <div
                        className="forecast-image-content"
                        onClick={() =>
                          openImageModal(
                            latestScanForecastData.forecastImages.shapPlotForecast_LesionVolGM,
                            "Grey Matter Lesion Prediction"
                          )
                        }
                      >
                        <img
                          src={latestScanForecastData.forecastImages.shapPlotForecast_LesionVolGM}
                          alt="Grey Matter Lesion Forecast"
                          className="forecast-shap-image"
                        />
                        <div className="image-overlay">
                          <span className="zoom-icon">🔍</span>
                          <span className="zoom-text">Click to expand</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Lesion Count Forecast */}
                  {latestScanForecastData.forecastImages.shapPlotForecast_Count && (
                    <div className="forecast-image-item">
                      <div className="forecast-image-header">
                        <span className="forecast-icon">🔢</span>
                        <span className="forecast-label">Lesion Count Prediction</span>
                      </div>
                      <div
                        className="forecast-image-content"
                        onClick={() =>
                          openImageModal(
                            latestScanForecastData.forecastImages.shapPlotForecast_Count,
                            "Lesion Count Prediction"
                          )
                        }
                      >
                        <img
                          src={latestScanForecastData.forecastImages.shapPlotForecast_Count}
                          alt="Lesion Count Forecast"
                          className="forecast-shap-image"
                        />
                        <div className="image-overlay">
                          <span className="zoom-icon">🔍</span>
                          <span className="zoom-text">Click to expand</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Grey Matter Volume Forecast */}
                  {latestScanForecastData.forecastImages.shapPlotForecast_GreyMatterVolume && (
                    <div className="forecast-image-item">
                      <div className="forecast-image-header">
                        <span className="forecast-icon">🔘</span>
                        <span className="forecast-label">Grey Matter Volume Prediction</span>
                      </div>
                      <div
                        className="forecast-image-content"
                        onClick={() =>
                          openImageModal(
                            latestScanForecastData.forecastImages.shapPlotForecast_GreyMatterVolume,
                            "Grey Matter Volume Prediction"
                          )
                        }
                      >
                        <img
                          src={
                            latestScanForecastData.forecastImages.shapPlotForecast_GreyMatterVolume
                          }
                          alt="Grey Matter Volume Forecast"
                          className="forecast-shap-image"
                        />
                        <div className="image-overlay">
                          <span className="zoom-icon">🔍</span>
                          <span className="zoom-text">Click to expand</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </Col>
        <Col md={5}>
          {activeTab === "analysis" ? (
            <>
              {scanData ? (
                <>
                  {/* Anatomical Breakdown */}
                  <div className="custom-card">
                    <div className="custom-card-header">
                      <div className="custom-card-header-text">
                        <h3>Anatomical Breakdown</h3>
                        <p>Lesion distribution by tissue type</p>
                      </div>
                    </div>
                    <div className="anatomical-breakdown">
                      {/* White Matter Lesions */}
                      <div className="tissue-lesion">
                        <div className="lesion-header">
                          <div className="lesion-icon white-matter">●</div>
                          <span className="lesion-label">Lesions in White Matter</span>
                          <span className="lesion-tag flair">FLAIR</span>
                        </div>
                        <div className="lesion-volume">
                          Volume:{" "}
                          {scanData.whiteMatterLesionVolume
                            ? `${(scanData.whiteMatterLesionVolume / 1000).toFixed(2)} mL`
                            : "N/A"}
                        </div>
                        <div className="progress-bar">
                          <div
                            className="progress-fill"
                            style={{
                              width: `${
                                scanData.whiteMatterLesionVolume
                                  ? Math.min(
                                      (scanData.whiteMatterLesionVolume / 1000 / 5) * 100,
                                      100
                                    )
                                  : 0
                              }%`,
                            }}
                          ></div>
                        </div>
                        <div className="lesion-details">
                          <span className="lesion-type">Type: T2-hyperintense</span>
                          <span className="lesion-tag t1-black">T1 Black Hole</span>
                          <span className="intensity">
                            Avg intensity: {scanData.whiteMatterIntensity || "N/A"}
                          </span>
                        </div>

                        {scanData.changeFromBaseline_totalLesionVolume ? (
                          <div className="biomarker-item">
                            <div className="biomarker-label">Lesion Volume Change</div>
                            <div
                              className={`biomarker-value ${
                                scanData.changeFromBaseline_totalLesionVolume > 0
                                  ? "increase"
                                  : "decrease"
                              }`}
                            >
                              {scanData.changeFromBaseline_totalLesionVolume > 0 ? "+" : ""}
                              {(scanData.changeFromBaseline_totalLesionVolume / 1000).toFixed(2)} mL
                            </div>
                            <div className="biomarker-description">
                              {scanData.changeFromBaseline_totalLesionVolume > 0
                                ? "Lesion volume increased"
                                : "Lesion volume decreased"}{" "}
                              from baseline
                            </div>
                          </div>
                        ) : (
                          <div className="biomarker-item">
                            <div className="biomarker-label">Lesion Volume Change</div>
                            <div className="biomarker-value">N/A</div>
                            <div className="biomarker-description">N/A</div>
                          </div>
                        )}

                        {scanData.changeFromBaseline_totalBrainVolume ? (
                          <div className="biomarker-item">
                            <div className="biomarker-label">Brain Volume Change</div>
                            <div
                              className={`biomarker-value ${
                                scanData.changeFromBaseline_totalBrainVolume > 0
                                  ? "increase"
                                  : "decrease"
                              }`}
                            >
                              {scanData.changeFromBaseline_totalBrainVolume > 0 ? "+" : ""}
                              {(scanData.changeFromBaseline_totalBrainVolume / 1000).toFixed(2)} mL
                            </div>
                            <div className="biomarker-description">
                              {scanData.changeFromBaseline_totalBrainVolume > 0
                                ? "Brain volume increased"
                                : "Brain volume decreased"}{" "}
                              from baseline
                            </div>
                          </div>
                        ) : (
                          <div className="biomarker-item">
                            <div className="biomarker-label">Brain Volume Change</div>
                            <div className="biomarker-value">N/A</div>
                            <div className="biomarker-description">N/A</div>
                          </div>
                        )}

                        {scanData.changeFromBaseline_totalLesionCount ? (
                          <div className="biomarker-item">
                            <div className="biomarker-label">Lesion Count Change</div>
                            <div
                              className={`biomarker-value ${
                                scanData.changeFromBaseline_totalLesionCount > 0
                                  ? "increase"
                                  : "decrease"
                              }`}
                            >
                              {scanData.changeFromBaseline_totalLesionCount > 0 ? "+" : ""}
                              {scanData.changeFromBaseline_totalLesionCount} lesions
                            </div>
                            <div className="biomarker-description">
                              {scanData.changeFromBaseline_totalLesionCount > 0
                                ? "New lesions detected"
                                : "Lesions resolved"}{" "}
                              since baseline
                            </div>
                          </div>
                        ) : (
                          <div className="biomarker-item">
                            <div className="biomarker-label">Lesion Count Change</div>
                            <div className="biomarker-value">N/A</div>
                            <div className="biomarker-description">N/A</div>
                          </div>
                        )}
                      </div>

                      {/* Grey Matter Lesions */}
                      <div className="tissue-lesion">
                        <div className="lesion-header">
                          <div className="lesion-icon grey-matter">●</div>
                          <span className="lesion-label">Lesions in Grey Matter</span>
                          <span className="lesion-tag flair">FLAIR</span>
                        </div>
                        <div className="lesion-volume">
                          Volume:{" "}
                          {scanData.greyMatterLesionVolume
                            ? `${(scanData.greyMatterLesionVolume / 1000).toFixed(2)} mL`
                            : "N/A"}
                        </div>
                        <div className="progress-bar">
                          <div
                            className="progress-fill"
                            style={{
                              width: `${
                                scanData.greyMatterLesionVolume
                                  ? Math.min(
                                      (scanData.greyMatterLesionVolume / 1000 / 2) * 100,
                                      100
                                    )
                                  : 0
                              }%`,
                            }}
                          ></div>
                        </div>
                        <div className="lesion-details">
                          <span className="lesion-type">Type: T2-hyperintense</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Early Biomarkers */}
                  <div className="custom-card mt-4">
                    <div className="custom-card-header">
                      <div className="custom-card-header-text">
                        <h3>Early Biomarkers</h3>
                      </div>
                    </div>
                    <div className="early-biomarkers">
                      {/* Total Lesion Load */}
                      <div className="biomarker-item">
                        <div className="biomarker-label">
                          Total Lesion Load - {scanData.timepoint}
                        </div>
                        <div className="biomarker-value">
                          {scanData.totalLesionVolume
                            ? `${(scanData.totalLesionVolume / 1000).toFixed(2)} mL`
                            : scanData.lesionLoad
                            ? `${(scanData.lesionLoad / 1000).toFixed(2)} mL`
                            : "N/A"}
                        </div>
                        <div className="progress-bar">
                          <div
                            className="progress-fill"
                            style={{
                              width: `${
                                scanData.totalLesionVolume || scanData.lesionLoad
                                  ? Math.min(
                                      ((scanData.totalLesionVolume || scanData.lesionLoad) /
                                        1000 /
                                        5) *
                                        100,
                                      100
                                    )
                                  : 0
                              }%`,
                            }}
                          ></div>
                        </div>
                        <div className="biomarker-description">
                          {scanData.totalLesionCount
                            ? `${scanData.totalLesionCount} lesions detected`
                            : "Within normal range for disease stage"}
                        </div>
                      </div>

                      {/* Brain Atrophy Rate */}
                      <div className="biomarker-item">
                        <div className="biomarker-label">
                          Brain Atrophy Rate - {scanData.timepoint}
                        </div>
                        <div className="biomarker-value atrophy">
                          {scanData.brainAtrophyRate || scanData.atrophyRate || "N/A"} mL/year
                        </div>
                        <div className="progress-bar">
                          <div
                            className="progress-fill"
                            style={{
                              width: `${
                                scanData.brainAtrophyRate && scanData.brainAtrophyRate !== "N/A"
                                  ? Math.min(
                                      (Math.abs(parseFloat(scanData.brainAtrophyRate)) / 10) * 100,
                                      100
                                    )
                                  : scanData.atrophyRate && scanData.atrophyRate !== "N/A"
                                  ? Math.min(
                                      (Math.abs(parseFloat(scanData.atrophyRate)) / 10) * 100,
                                      100
                                    )
                                  : 0
                              }%`,
                            }}
                          ></div>
                        </div>
                        <div className="biomarker-description">
                          {(scanData.brainAtrophyRate &&
                            scanData.brainAtrophyRate !== "N/A" &&
                            parseFloat(scanData.brainAtrophyRate) < -3) ||
                          (scanData.atrophyRate &&
                            scanData.atrophyRate !== "N/A" &&
                            parseFloat(scanData.atrophyRate) < -3)
                            ? "Elevated atrophy rate"
                            : "Slightly elevated compared to controls"}
                        </div>
                      </div>

                      {/* Grey Matter Volume */}
                      <div className="biomarker-item">
                        <div className="biomarker-label">
                          Grey Matter Volume - {scanData.timepoint}
                        </div>
                        <div className="biomarker-value grey">
                          {scanData.greyMatterVolume
                            ? `${(scanData.greyMatterVolume / 1000).toFixed(2)} mL`
                            : "N/A"}
                        </div>
                        <div className="progress-bar">
                          <div
                            className="progress-fill"
                            style={{
                              width: `${
                                scanData.greyMatterVolume
                                  ? Math.min((scanData.greyMatterVolume / 1000 / 800) * 100, 100)
                                  : 0
                              }%`,
                            }}
                          ></div>
                        </div>
                        <div className="biomarker-description">
                          {scanData.greyMatterVolume
                            ? "Dynamic grey matter measurement"
                            : "Normal grey matter preservation"}
                        </div>
                      </div>

                      {/* White Matter Volume */}
                      <div className="biomarker-item">
                        <div className="biomarker-label">
                          White Matter Volume - {scanData.timepoint}
                        </div>
                        <div className="biomarker-value white">
                          {scanData.whiteMatterVolume
                            ? `${(scanData.whiteMatterVolume / 1000).toFixed(2)} mL`
                            : "N/A"}
                        </div>
                        <div className="progress-bar">
                          <div
                            className="progress-fill"
                            style={{
                              width: `${
                                scanData.whiteMatterVolume
                                  ? Math.min((scanData.whiteMatterVolume / 1000 / 600) * 100, 100)
                                  : 0
                              }%`,
                            }}
                          ></div>
                        </div>
                        <div className="biomarker-description">
                          {scanData.whiteMatterVolume
                            ? "Dynamic white matter measurement"
                            : "Normal white matter preservation"}
                        </div>
                      </div>

                      {/* Change from Baseline Section */}
                      {(scanData.changeFromBaseline_totalLesionVolume ||
                        scanData.changeFromBaseline_totalBrainVolume) && (
                        <div className="change-from-baseline-section">
                          <div className="baseline-header">
                            <h4>Change from Baseline</h4>
                          </div>
                          {scanData.changeFromBaseline_totalLesionVolume && (
                            <div className="biomarker-item">
                              <div className="biomarker-label">Lesion Volume Change</div>
                              <div
                                className={`biomarker-value ${
                                  scanData.changeFromBaseline_totalLesionVolume > 0
                                    ? "increase"
                                    : "decrease"
                                }`}
                              >
                                {scanData.changeFromBaseline_totalLesionVolume > 0 ? "+" : ""}
                                {(scanData.changeFromBaseline_totalLesionVolume / 1000).toFixed(
                                  2
                                )}{" "}
                                mL
                              </div>
                              <div className="biomarker-description">
                                {scanData.changeFromBaseline_totalLesionVolume > 0
                                  ? "Lesion volume increased"
                                  : "Lesion volume decreased"}{" "}
                                from baseline
                              </div>
                            </div>
                          )}

                          {scanData.changeFromBaseline_totalBrainVolume && (
                            <div className="biomarker-item">
                              <div className="biomarker-label">Brain Volume Change</div>
                              <div
                                className={`biomarker-value ${
                                  scanData.changeFromBaseline_totalBrainVolume > 0
                                    ? "increase"
                                    : "decrease"
                                }`}
                              >
                                {scanData.changeFromBaseline_totalBrainVolume > 0 ? "+" : ""}
                                {(scanData.changeFromBaseline_totalBrainVolume / 1000).toFixed(
                                  2
                                )}{" "}
                                mL
                              </div>
                              <div className="biomarker-description">
                                {scanData.changeFromBaseline_totalBrainVolume > 0
                                  ? "Brain volume increased"
                                  : "Brain volume decreased"}{" "}
                                from baseline
                              </div>
                            </div>
                          )}

                          {scanData.changeFromBaseline_totalLesionCount && (
                            <div className="biomarker-item">
                              <div className="biomarker-label">Lesion Count Change</div>
                              <div
                                className={`biomarker-value ${
                                  scanData.changeFromBaseline_totalLesionCount > 0
                                    ? "increase"
                                    : "decrease"
                                }`}
                              >
                                {scanData.changeFromBaseline_totalLesionCount > 0 ? "+" : ""}
                                {scanData.changeFromBaseline_totalLesionCount} lesions
                              </div>
                              <div className="biomarker-description">
                                {scanData.changeFromBaseline_totalLesionCount > 0
                                  ? "New lesions detected"
                                  : "Lesions resolved"}{" "}
                                since baseline
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </>
              ) : null}
            </>
          ) : (
            <>
              {latestScanForecastData ? (
                <ForecastDisplay scanData={latestScanForecastData} />
              ) : (
                <div className="custom-card">
                  <div className="custom-card-header">
                    <div className="custom-card-header-text">
                      <h3>No Forecast Available</h3>
                      <p>
                        Forecast predictions require baseline data for comparison. This appears to
                        be a baseline scan.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </Col>
      </Row>

      {/* Image Modal */}
      {selectedImage && (
        <div className="image-modal-overlay" onClick={closeImageModal}>
          <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="image-modal-header">
              <h4>{selectedImage.title}</h4>
              <div className="modal-controls">
                <button className="zoom-btn" onClick={zoomOut} title="Zoom Out">
                  <span>-</span>
                </button>
                <span className="zoom-level">{Math.round(imageZoom * 100)}%</span>
                <button className="zoom-btn" onClick={zoomIn} title="Zoom In">
                  <span>+</span>
                </button>
                <button className="reset-btn" onClick={resetZoom} title="Reset Zoom">
                  <span>🔄</span>
                </button>
                <button className="close-btn" onClick={closeImageModal} title="Close">
                  <span>✕</span>
                </button>
              </div>
            </div>
            <div className="image-modal-body">
              <img
                src={selectedImage.url}
                alt={selectedImage.title}
                className="modal-image"
                style={{
                  transform: `scale(${imageZoom})`,
                  transformOrigin: "center center",
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
